import { MouseEvent, Ref, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpandMore, EventNote } from '@mui/icons-material';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Card,
  CardActionArea,
  FormControlLabel,
  Typography,
  Stack,
} from '@mui/material';

import { DetailedRoute } from '@/generated/model/detailedRoute';
import { ctxPluto } from '@/contexts/PlutoContext';
import { RouteEventDetail } from './RouteEventDetail';
import { ctxRoutes } from '@/contexts/RoutesCtx.tsx';
import { RouteInfoCard } from './RouteInfoCard';

import './RouteStyle.css';

export function RouteDetail({
  route,
  className,
  ref,
}: {
  route: DetailedRoute | undefined;
  className: string;
  ref: Ref<HTMLDivElement>;
}) {
  const { t } = useTranslation();
  const ctx = useContext(ctxPluto);
  const ctxRoute = useContext(ctxRoutes);
  const [openEvents, setOpenEvents] = useState<boolean>(false);
  const [isHighlighted, setHightlighted] = useState<boolean>(
    route && ctxRoute?.highlightedRoutes ? ctxRoute.highlightedRoutes.includes(route.id) : false
  );

  useEffect(() => {
    if (!route) return;
    if (!ctxRoute?.highlightedRoutes?.includes(route.id)) {
      setHightlighted(false);
    }
  }, [ctxRoute?.highlightedRoutes, route]);

  if (!ctx) return;

  if (!route) {
    return (
      <Typography variant="subtitle2" sx={{ textAlign: 'center', my: 1 }} data-qa-element-id="no_route_label">
        {t('routes.emptyDay')}
      </Typography>
    );
  }

  function toggleAndSortHighlightedRoutes({
    currentHighlighted,
    clickedRouteId,
    allRoutes,
  }: {
    currentHighlighted: number[] | undefined;
    clickedRouteId: number;
    allRoutes: number[] | undefined;
  }) {
    let updated: number[];
    if (!allRoutes || !currentHighlighted) return undefined;

    if (currentHighlighted.includes(clickedRouteId)) {
      updated = currentHighlighted.filter((id) => id !== clickedRouteId);
    } else {
      updated = [...currentHighlighted, clickedRouteId];
    }

    return updated.sort(
      (a, b) => allRoutes.findIndex((route) => route === a) - allRoutes.findIndex((route) => route === b)
    );
  }

  const header = (
    <>
      {route.driver && (
        <Stack
          direction="row"
          sx={{ gap: 1, pl: 1, alignItems: 'center', pb: 1 }}
          data-qa-element-id="route_driver_header"
        >
          <img className="route-driver-icon" src="/svg/steeringwheel.svg" />
          <Typography variant="subtitle2" data-qa-element-id="driver_name_label">
            {route.driver.name}
          </Typography>
        </Stack>
      )}
    </>
  );

  const footer = (
    <>
      {route.events && route.events.length > 0 && (
        <Accordion
          onChange={() => setOpenEvents(!openEvents)}
          onClick={(e: MouseEvent) => e.stopPropagation()}
          sx={{
            borderRadius: 'var(--mui-shape-borderRadius) !important',
            margin: '0px !important',
            '&::before': { display: 'none' },
          }}
          data-qa-element-id="route_events_dropdown"
        >
          <AccordionSummary
            component="div"
            expandIcon={<ExpandMore />}
            sx={{
              minHeight: '40px',
              '&.Mui-expanded': {
                minHeight: '40px',
              },
              '& .MuiAccordionSummary-content': {
                margin: '8px 0',
              },
              '& .MuiAccordionSummary-content.Mui-expanded': {
                margin: '8px 0',
              },
            }}
          >
            <FormControlLabel
              control={
                <Badge
                  className="route-event-control"
                  color="primary"
                  badgeContent={route.events?.length || 0}
                  sx={{ '& .MuiBadge-badge': { right: -2, top: 12, border: `2px solid white` } }}
                  slotProps={{
                    badge: {
                      id: 'event_count_label',
                    },
                  }}
                >
                  <EventNote />
                </Badge>
              }
              label={t('routes.events')}
              disabled={route.events ? route.events.length === 0 : true}
            />
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }} data-qa-element-id="route_events_detail_panel">
            <RouteEventDetail events={route.events} />
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );

  return (
    <Card className={`route-detail ${className}`} ref={ref} data-qa-element-id="route_panel">
      <CardActionArea
        disableRipple
        className={isHighlighted ? 'route-detail-highlighted' : undefined}
        onClick={() => {
          ctxRoute?.setHighlightedRoutes((prev) =>
            toggleAndSortHighlightedRoutes({
              currentHighlighted: prev,
              clickedRouteId: route.id,
              allRoutes: ctxRoute.routes?.map((r) => r.id),
            })
          );

          setHightlighted(!isHighlighted);
        }}
      >
        <RouteInfoCard route={route} header={header} footer={footer} />
      </CardActionArea>
    </Card>
  );
}
